<script setup lang="ts">
// The overview of one node: inputs, then each encode with its destinations
// nested and collapsible, then the outputs (each playing an input, encode
// or destination), then the machine.
// A player node (mode player) shows its inputs, its screen and the machine.
// Rows are EntityRow (shared with the fleet page); facts come from the live
// status in the store. A collapsed (or expanded) encode's bar also carries
// markers for its destinations' problems, so simultaneous failures stand out.
import { computed, ref } from 'vue'
import type { Event } from '../api'
import { useNode } from '../store'
import {
    ago,
    at,
    cpuShare,
    outputKind,
    fpsLabel,
    eventsIn,
    gbText,
    mbps,
    okAsRunning,
    pct,
    playsName,
    urlPort,
} from '../util'
import EntityRow from './EntityRow.vue'
import { monitorBadge } from '../monitoring'
import { hasFeature } from '../features'
import { useReorder } from '../reorder'
import TimeAxis from './TimeAxis.vue'
import EventDialog from './EventDialog.vue'
import type { EditTarget } from '../api'

const emit = defineEmits<{ add: [EditTarget] }>()
const node = useNode()
const cfg = computed(() => node.config!)
const st = computed(() => node.status!)
const player = computed(() => cfg.value.mode === 'player')
// rows dragged (or Alt+↑/↓) into another order, each list on its own: a
// destination among its encode's
const movable = computed(() => !node.readOnly && hasFeature(node.features, 'reorder'))
const inputRow = useReorder((ids) => node.reorder('inputs', ids))
const outputRow = useReorder((ids) => node.reorder('outputs', ids))
const encodeRow = useReorder((ids) => node.reorder('encodes', ids))
const destRow = useReorder((ids) => node.reorder('destinations', ids))
const ids = (l: { id: string }[]) => () => l.map((x) => x.id)
const dialog = ref<{ events: Event[]; from: number; to: number; title: string; all: Event[] } | null>(null)
// events of an entity (and, for encodes, of its destinations) inside a window
function openWindow(
    kind: 'input' | 'encode' | 'destination' | 'output' | 'machine' | 'storage',
    id: string,
    name: string,
    w: { from: number; to: number },
) {
    // the entity's events as its bar draws them (unknown while noded was
    // down), its destinations' with them
    const ids = new Set(kind === 'encode' ? destsOf(id).map((d) => d.id) : [])
    const src = [node.entityEvents(kind, id), ...[...ids].map((d) => node.entityEvents('destination', d))]
        .flat()
        .sort((a, b) => at(a) - at(b))
    dialog.value = { events: eventsIn(src, kind, id, ids, w), from: w.from, to: w.to, title: name, all: src }
}

function inputFact(id: string) {
    const s = st.value.inputs[id]
    const c = cfg.value.inputs.find((i) => i.id === id)!
    if (!s) return ''
    if (s.state === 'disabled' && c.disabled) return 'disabled: receives nothing'
    if (s.state === 'no_signal') return `no signal ${ago(s.since, node.now)}`
    if (s.state === 'black') return `black and silent ${ago(s.since, node.now)}`
    if (s.state === 'error') return s.error || 'error'
    if (player.value && s.state === 'stopped') return 'not on screen' + where(c)
    // auto parts from the source as capture reports it ("1920x1080 59.94 fps")
    const m = s.source?.match(/^(\d+)x(\d+) ([\d.]+)/)
    const size = c.width ? `${c.width}×${c.height}` : m ? `${m[1]}×${m[2]}` : 'auto'
    // a rate read from the source always to two decimals: "20" then
    // "19.98" made everything after it jump
    const fps = c.fps ? fpsLabel(c.fps) : m ? Number(m[3]).toFixed(2) : ''
    // HDR, declared or as the source is tagged ("… fps hlg"); SDR says nothing
    const hdr = c.colour ? c.colour : (s.source?.match(/ (hlg|pq)\b/)?.[1] ?? '')
    const deep = c.bit_depth ? c.bit_depth === 10 : / 10bit\b/.test(s.source ?? '')
    const colour = (deep ? ' 10-bit' : '') + (hdr && hdr !== 'sdr' ? ` ${hdr.toUpperCase()}` : '')
    const port =
        c.kind === 'srt_listener'
            ? ` · :${c.port}`
            : c.kind === 'rtmp_listener'
              ? ` · :${urlPort(c.url, 1935)}`
              : c.kind === 'multiview'
                ? ` · multiview ${c.multiview?.layout}`
                : ''
    if (player.value) return `${size} ${fps}${colour} · on screen`.trim() + where(c)
    const use = ` · cpu ${pct(cpuShare(s.cpu_cores ?? 0, st.value.machine.specs.cores))}${s.gpu ? ` · nvdec ${s.gpu.dec}%` : ''}`
    // what stays put first, the figures that change last: a figure growing
    // a digit moves nothing after it
    return `${size} ${fps}${colour}`.trim() + port + (c.slate_enabled ? ' · slate' : '') + use
}
// where a player's input comes from, short: the multicast group or the host
const where = (c: { kind: string; url?: string; port?: number }) =>
    c.kind === 'srt_listener'
        ? ` · :${c.port}`
        : c.url
          ? ` · ${c.url.replace(/^\w+:\/\/@?/, '').replace(/[/?].*$/, '')}`
          : ''
function screenFact(id: string) {
    const s = st.value.outputs?.[id]
    const o = cfg.value.outputs?.find((x) => x.id === id)
    if (!s || !o) return ''
    const shown = cfg.value.inputs.find((i) => i.id === o.input_id)?.name
    if (s.state === 'disabled') return 'disabled'
    if (s.state === 'error') return s.error || 'error'
    if (!shown) return 'nothing on screen'
    if (s.state === 'no_signal') return `${shown} · no signal ${ago(s.since, node.now)}`
    if (s.state !== 'running') return `${shown} · ${s.state.replace('_', ' ')}`
    return `${shown} · ${s.fps.toFixed(2)} fps${s.decoder ? ' · ' + s.decoder : ''}${s.dropped_frames ? ` · ${s.dropped_frames} dropped` : ''}`
}
const storageSnap = (id: string) => st.value.storage?.find((x) => x.id === id)
function storageFact(id: string) {
    const c = cfg.value.storage?.find((x) => x.id === id)
    const s = storageSnap(id)
    const where = !c
        ? ''
        : c.kind === 'disk'
          ? `drive · ${c.fstype}`
          : `${c.kind} ${c.kind === 'smb' ? `//${c.server}/${c.share}` : `${c.server}:${c.share}`}`
    if (!s || s.state === 'starting') return where
    if (s.state !== 'ok') return `${where} · ${s.error || s.state.replace('_', ' ')}`
    return `${where} · ${gbText(s.free_gb)} free`
}
function encodeFact(id: string) {
    const s = st.value.encodes[id]
    if (!s) return ''
    if (s.state === 'disabled') return s.error ? `disabled: ${s.error}` : 'disabled' // "its input is disabled"
    if (s.state === 'error') return s.error || 'error'
    if (s.state === 'stopped') return `stopped: input has no signal ${ago(s.since, node.now)}`
    if (s.state === 'no_input') return 'no input: pick one in its settings'
    if (s.state !== 'running') return s.state.replace('_', ' ')
    const e = cfg.value.encodes.find((x) => x.id === id)!
    const input = st.value.inputs[e.input_id]
    // a passthrough reads its input's receiver in process: no CPU of its own
    const use = e.passthrough
        ? ''
        : ` · cpu ${pct(cpuShare(s.cpu_cores ?? 0, st.value.machine.specs.cores))}${s.gpu ? ` · nvenc ${s.gpu.enc}%` : ''}`
    const notes = `${input?.state === 'no_signal' ? 'input has no signal · ' : ''}${e.slate_enabled ? 'slate · ' : ''}`
    const tw = s.twitch ? ` · twitch ${s.twitch.renditions.length} renditions` : ''
    return `${notes}${s.fps.toFixed(2)} fps · ${mbps(s.bitrate_k)}${tw}${use}`
}
function destFact(id: string) {
    const s = st.value.destinations[id]
    if (!s) return ''
    const d = cfg.value.destinations.find((x) => x.id === id)
    const pull = d?.kind === 'rtmp_pull' || d?.kind === 'srt_listener'
    const delay =
        (s.delay_seconds ? `${s.delay_seconds} s` : 'live') +
        (pull
            ? `${s.port ? ` · :${s.port}` : ''} · ${s.clients ?? 0}${d?.max_clients ? `/${d.max_clients}` : ''} client${s.clients === 1 ? '' : 's'}`
            : '')
    if (s.state === 'disabled') return `disabled · ${delay}`
    if (s.state === 'stopped') return `stopped with its encode · ${delay}`
    if (s.state === 'error') return `${delay} · ${s.error || 'error'}`
    if (s.state === 'connecting') return `${delay} · connecting`
    return `${delay}${s.restarts ? ` · ${s.restarts} reconnect${s.restarts > 1 ? 's' : ''}` : ''}`
}
// an encode's dot is its own state: a destination's trouble shows on the
// destination's row, on the encode's bar (markers) and, collapsed, in its
// summary, not as a yellow encode (Niels)
const encodeState = (id: string) => st.value.encodes[id]?.state ?? ''
const destsOf = (encodeId: string) => cfg.value.destinations.filter((d) => d.encode_id === encodeId)
function outputFact(id: string, kind: string) {
    const s = st.value.outputs?.[id]
    if (!s) return ''
    if (s.state === 'disabled') return 'disabled'
    if (s.state === 'error') return s.error || 'error'
    if (s.state === 'running' && (kind === 'alsa' || kind === 'aes67')) return kind === 'aes67' ? 'sending' : 'playing'
    return s.state === 'running' ? `${s.fps.toFixed(2)} fps` : s.state.replace('_', ' ')
}
// destination problems for the encode bar, and a summary for the collapsed row
function destEvents(encodeId: string): Event[] {
    const ids = new Set(destsOf(encodeId).map((d) => d.id))
    return (node.live ? node.events : node.windowEvents).filter((e) => e.kind === 'destination' && ids.has(e.entity))
}
function destSummary(encodeId: string) {
    const ds = destsOf(encodeId)
    if (!ds.length) return ''
    const bad = ds.filter((d) => st.value.destinations[d.id]?.state === 'error').length
    const on = ds.filter((d) => st.value.destinations[d.id]?.state === 'connected').length
    return `${on}/${ds.length} sending${bad ? ` · ${bad} failing` : ''}`
}
const isSel = (kind: string, id: string) => node.selection?.kind === kind && node.selection.id === id
const m = computed(() => st.value.machine)
// the machine's problem, short (the full text is in its detail panel)
const machineExtra = computed(() => {
    const listed = [...(m.value?.faults ?? []), ...(m.value?.hardware ?? [])] // faults first
    const list = listed.length ? listed : m.value?.state !== 'ok' && m.value?.error ? [m.value.error] : []
    if (!list.length) return ''
    const first = list[0]!.split(': ')[0]!
    return list.length > 1 ? `${first} +${list.length - 1} more` : first
})
// monitoring locked, or how many watch (the Monitoring tab has the rest)
const watching = computed(() => (player.value ? null : monitorBadge(st.value.monitor)))
// percentages two digits wide (a figure space before one digit): each
// figure keeps its place as the load moves between 9 and 10 %
const p2 = (v: number) => String(Math.round(v)).padStart(2, '\u2007') + '%'
const machineFact = computed(
    () =>
        `cpu ${p2(m.value?.cpu_pct ?? 0)} · mem ${p2(m.value?.mem_pct ?? 0)}` +
        (m.value?.gpus ?? []).map((g) => ` · gpu${g.index} ${p2(g.util_pct)}`).join('') +
        (m.value?.disks ?? []).map((d) => ` · ${d.path === '/' ? 'disk' : d.path} ${p2(d.used_pct)}`).join(''),
)
</script>

<template>
    <!-- no overflow here: App.vue makes it scroll (a class here would win over it) -->
    <div class="card">
        <div class="entity-row border-t-0 pt-1.5 pb-0"><span /><span /><span /><TimeAxis /></div>
        <div class="section-head">
            <span class="min-w-0 flex-1">Inputs</span
            ><button v-if="!node.readOnly" class="btn-ghost text-xs" @click="emit('add', { kind: 'input' })">
                + input
            </button>
        </div>
        <EntityRow
            v-for="i in cfg.inputs"
            :id="i.id"
            :key="i.id"
            kind="input"
            :name="i.name"
            v-bind="inputRow(ids(cfg.inputs), i.id, movable)"
            :fact="inputFact(i.id)"
            :state="st.inputs[i.id]?.state ?? ''"
            :selected="isSel('input', i.id)"
            @select="node.select({ kind: 'input', id: i.id })"
            @open="openWindow('input', i.id, i.name, $event)"
        />
        <div v-if="!cfg.inputs.length" class="px-3 py-2.5 text-fg-3">
            {{
                player
                    ? 'No inputs yet. Add the streams this screen may show: SRT, RTMP, multicast or a test pattern.'
                    : 'No inputs yet. Add an SRT, RTMP, DeckLink or test input.'
            }}
        </div>

        <template v-if="player">
            <div class="section-head">
                <span class="min-w-0 flex-1">Screen</span
                ><button
                    v-if="!node.readOnly && !cfg.outputs?.length"
                    class="btn-ghost text-xs"
                    @click="emit('add', { kind: 'output' })"
                >
                    + screen
                </button>
            </div>
            <EntityRow
                v-for="o in cfg.outputs ?? []"
                :id="o.id"
                :key="o.id"
                kind="output"
                :name="o.name"
                tag="HDMI"
                :fact="screenFact(o.id)"
                :state="st.outputs?.[o.id]?.state ?? ''"
                :selected="isSel('output', o.id)"
                @select="node.select({ kind: 'output', id: o.id })"
                @open="openWindow('output', o.id, o.name, $event)"
            />
            <div v-if="!cfg.outputs?.length" class="px-3 py-2.5 text-fg-3">
                No screen yet. Add the HDMI output to show an input on it.
            </div>
        </template>

        <div v-if="!player" class="section-head">
            <span class="min-w-0 flex-1">Encodes</span
            ><button
                v-if="!node.readOnly"
                class="btn-ghost text-xs"
                :disabled="!cfg.inputs.length"
                @click="emit('add', { kind: 'encode' })"
            >
                + encode
            </button>
        </div>
        <!-- an encode with its destinations, moved as one -->
        <div v-for="e in cfg.encodes" :key="e.id" v-bind="encodeRow(ids(cfg.encodes), e.id, movable)">
            <EntityRow
                :id="e.id"
                kind="encode"
                :name="e.name"
                :fact="encodeFact(e.id)"
                :extra="node.collapsed.has(e.id) ? destSummary(e.id) : ''"
                :state="encodeState(e.id)"
                collapsible
                :collapsed="node.collapsed.has(e.id)"
                :overlay="destEvents(e.id)"
                :selected="isSel('encode', e.id)"
                @toggle="node.toggleCollapse(e.id)"
                @select="node.select({ kind: 'encode', id: e.id })"
                @open="openWindow('encode', e.id, e.name + ' and its destinations', $event)"
            />
            <template v-if="!node.collapsed.has(e.id)">
                <EntityRow
                    v-for="d in destsOf(e.id)"
                    :id="d.id"
                    :key="d.id"
                    kind="destination"
                    :name="d.name"
                    v-bind="destRow(ids(destsOf(e.id)), d.id, movable)"
                    :fact="destFact(d.id)"
                    :state="st.destinations[d.id]?.state ?? ''"
                    nested
                    :selected="isSel('destination', d.id)"
                    @select="node.select({ kind: 'destination', id: d.id })"
                    @open="openWindow('destination', d.id, d.name, $event)"
                />
                <div
                    v-if="!node.readOnly"
                    class="group entity-row cursor-pointer py-0.5 text-xs hover:bg-bg-2"
                    role="button"
                    tabindex="0"
                    @click="emit('add', { kind: 'destination', encodeId: e.id })"
                    @keydown.enter.prevent="emit('add', { kind: 'destination', encodeId: e.id })"
                    @keydown.space.prevent="emit('add', { kind: 'destination', encodeId: e.id })"
                >
                    <span /><span /><span class="pl-4.5 text-fg-3 group-hover:text-fg">+ destination</span><span />
                </div>
            </template>
        </div>
        <div v-if="!cfg.encodes.length && !player" class="px-3 py-2.5 text-fg-3">No encodes yet.</div>

        <!-- SDI, NDI and sound outputs: each plays an input, an encode or a
             destination (DECISIONS 2026-11-05); a player's screen is above -->
        <template v-if="!player">
            <div class="section-head">
                <span class="min-w-0 flex-1">Outputs</span
                ><button v-if="!node.readOnly" class="btn-ghost text-xs" @click="emit('add', { kind: 'output' })">
                    + output
                </button>
            </div>
            <EntityRow
                v-for="o in cfg.outputs ?? []"
                :id="o.id"
                :key="o.id"
                kind="output"
                :name="o.name"
                v-bind="outputRow(ids(cfg.outputs ?? []), o.id, movable)"
                :fact="`${outputKind(o.kind)} out · ${playsName(cfg, o)} · ${outputFact(o.id, o.kind)}`"
                :state="st.outputs?.[o.id]?.state ?? ''"
                :selected="isSel('output', o.id)"
                @select="node.select({ kind: 'output', id: o.id })"
                @open="openWindow('output', o.id, o.name, $event)"
            />
            <div v-if="!cfg.outputs?.length" class="px-3 py-2.5 text-fg-3">
                No outputs yet. Play an input, an encode or a destination on SDI or as an NDI source.
            </div>
        </template>

        <div class="section-head">
            <span class="min-w-0 flex-1">Machine</span>
        </div>
        <EntityRow
            id="node"
            kind="machine"
            name=""
            :fact="machineFact"
            :extra="machineExtra"
            :state="okAsRunning(m?.state)"
            :selected="isSel('machine', 'node')"
            @select="node.select({ kind: 'machine', id: 'node' })"
            @open="openWindow('machine', 'node', 'machine', $event)"
            ><span v-if="watching" :class="watching.tone" :title="watching.title" data-test="machine-monitoring">
                · {{ watching.text }}</span
            ></EntityRow
        >

        <div v-if="!player" class="section-head">
            <span class="min-w-0 flex-1">Storage</span
            ><button v-if="!node.readOnly" class="btn-ghost text-xs" @click="emit('add', { kind: 'storage' })">
                + storage
            </button>
        </div>
        <EntityRow
            v-for="s in cfg.storage ?? []"
            :id="s.id"
            :key="s.id"
            kind="storage"
            :name="s.name || s.id"
            :fact="storageFact(s.id)"
            :state="okAsRunning(storageSnap(s.id)?.state)"
            :selected="isSel('storage', s.id)"
            @select="node.select({ kind: 'storage', id: s.id })"
            @open="openWindow('storage', s.id, s.name || s.id, $event)"
        />
        <div v-if="!cfg.storage?.length && !player" class="px-3 py-2.5 text-fg-3">
            No network storage. Add a NAS to record onto it.
        </div>
        <EventDialog
            v-if="dialog"
            :events="dialog.events"
            :from="dialog.from"
            :to="dialog.to"
            :title="dialog.title"
            :all="dialog.all"
            @close="dialog = null"
        />
    </div>
</template>
